import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export default function AddExpensePage() {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { toast } = useToast();
  
  // Form state
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [categoryId, setCategoryId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // iOS date picker state
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(date.getMonth());
  const [selectedDay, setSelectedDay] = useState(date.getDate());
  const [selectedYear, setSelectedYear] = useState(date.getFullYear());

  // Arrays for picker values
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  // Generate arrays of days (1-31) and years (current year - 5 to current year + 5)
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const years = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i);
  
  // Get categories from the database
  const { data: categories } = useQuery({
    queryKey: ["categories", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("organization_id", organizationId);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!organizationId,
  });

  // Function to open the iOS-style date picker
  const openDatePicker = () => {
    setSelectedMonth(date.getMonth());
    setSelectedDay(date.getDate());
    setSelectedYear(date.getFullYear());
    setDatePickerOpen(true);
  };

  // Function to apply the selected date
  const applySelectedDate = () => {
    try {
      // Create a new Date object with the selected values
      const newDate = new Date(selectedYear, selectedMonth, selectedDay);
      setDate(newDate);
      setDatePickerOpen(false);
    } catch (error) {
      console.error("Error applying date:", error);
      toast({
        title: "Invalid Date",
        description: "Please select a valid date",
        variant: "destructive",
      });
    }
  };
  
  // Function to handle form submission
  const handleAddExpense = async () => {
    if (!description || !amount || !organizationId) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      
      // Create the expense
      const { error } = await supabase
        .from("expenses")
        .insert({
          description,
          amount: parseFloat(amount),
          date: format(date, 'yyyy-MM-dd'),
          category_id: categoryId ? parseInt(categoryId) : null,
          remarks,
          currency,
          organization_id: organizationId,
          user_id: user.id
        });
      
      if (error) throw error;
      
      // Show success message
      toast({
        title: "Expense added",
        description: "Your expense was added successfully",
      });
      
      // Navigate back to expenses page
      navigate(`/app/${organizationId}/expenses`);
    } catch (error) {
      console.error("Error adding expense:", error);
      toast({
        title: "Error",
        description: "Failed to add expense. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="container max-w-md mx-auto pt-4 pb-20">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Add New Expense</h1>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(`/app/${organizationId}/expenses`)}
          className="rounded-full"
        >
          <span className="sr-only">Close</span>
          <span className="text-lg font-medium">×</span>
        </Button>
      </div>
      
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="ios-input h-12"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select
              value={currency}
              onValueChange={setCurrency}
            >
              <SelectTrigger id="currency" className="ios-input h-12">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
                <SelectItem value="GBP">GBP (£)</SelectItem>
                <SelectItem value="CAD">CAD (CA$)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="category">Category</Label>
          <Select
            value={categoryId}
            onValueChange={setCategoryId}
          >
            <SelectTrigger id="category" className="ios-input h-12">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {categories?.map((category) => (
                <SelectItem key={category.id} value={String(category.id)}>
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: category.color }}
                    />
                    {category.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What was this expense for?"
            className="ios-input h-12"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="date">Date</Label>
          <Button
            id="date"
            variant="outline"
            onClick={openDatePicker}
            className="w-full justify-start text-left font-normal ios-input h-12"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {format(date, "MMMM d, yyyy")}
          </Button>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="remarks">Remarks</Label>
          <Textarea
            id="remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Additional notes (optional)"
            rows={3}
            className="ios-input"
          />
        </div>
        
        <div className="pt-4 space-y-3">
          <Button 
            onClick={handleAddExpense}
            disabled={isSubmitting}
            className="w-full bg-indigo-500 hover:bg-indigo-600 h-12 rounded-xl"
          >
            {isSubmitting ? "Adding..." : "Add Expense"}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => navigate(`/app/${organizationId}/expenses`)}
            className="w-full h-12 rounded-xl"
          >
            Cancel
          </Button>
        </div>
      </div>
      
      {/* iOS-Style Date Picker Sheet - Fixed 'X' text issue and styling */}
      <Sheet open={datePickerOpen} onOpenChange={setDatePickerOpen}>
        <SheetContent side="bottom" className="py-0 px-0 max-h-[90vh]">
          <div className="ios-date-picker">
            <div className="ios-date-picker-header border-b p-4 flex justify-between">
              <button 
                onClick={() => setDatePickerOpen(false)}
                className="text-gray-500 font-medium"
              >
                Cancel
              </button>
              <h3 className="text-lg font-semibold">Select Date</h3>
              <button 
                onClick={applySelectedDate}
                className="text-blue-500 font-medium"
              >
                Set
              </button>
            </div>
            
            <div className="ios-date-picker-wheels p-4 flex justify-center">
              <div className="ios-date-picker-wheel flex-1 overflow-auto text-center">
                {months.map((month, index) => (
                  <div 
                    key={month} 
                    className={`py-2 ${selectedMonth === index ? 'selected-date' : 'text-gray-400'}`}
                    onClick={() => setSelectedMonth(index)}
                  >
                    {month}
                  </div>
                ))}
              </div>
              
              <div className="ios-date-picker-wheel flex-1 overflow-auto text-center">
                {days.map((day) => (
                  <div 
                    key={day} 
                    className={`py-2 ${selectedDay === day ? 'selected-date' : 'text-gray-400'}`}
                    onClick={() => setSelectedDay(day)}
                  >
                    {day}
                  </div>
                ))}
              </div>
              
              <div className="ios-date-picker-wheel flex-1 overflow-auto text-center">
                {years.map((year) => (
                  <div 
                    key={year} 
                    className={`py-2 ${selectedYear === year ? 'selected-date' : 'text-gray-400'}`}
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
